/* Graphiques du Cockpit CFO — portés du prototype de design (SVG maison),
   branchés sur les vraies séries et habillés avec les tokens de la charte
   (var(--color-*)). Géométrie fidèle au handoff hifi. Toutes les valeurs
   monétaires arrivent en CHF ; `div` (1000 pour kCHF) ne sert qu'aux libellés,
   la géométrie étant normalisée (min/max). Donnée absente → point/segment omis. */

import React from 'react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';

const F = "'Inter', system-ui, sans-serif";

/* Couleur de statut → token charte. */
export const STATUS_COLOR = {
  favorable: 'var(--color-sage)',
  attention: 'var(--color-amber)',
  critique: 'var(--color-terracotta)',
};
const INK = 'var(--color-navy)';
const MUTED = 'var(--color-text-muted)';
const GRID = 'rgba(0,0,0,.07)';
const GRID_SOFT = 'rgba(0,0,0,.045)';

export function fmtNum(v, div = 1) {
  if (v == null || !Number.isFinite(Number(v))) return '–';
  const n = Math.round(Number(v) / div);
  const neg = n < 0;
  const s = Math.abs(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  return (neg ? '−' : '') + s;
}

function trendLine(arr) {
  const pts = arr.map((v, i) => [i, v]).filter((p) => Number.isFinite(p[1]));
  const n = pts.length;
  if (n < 2) return arr.map(() => null);
  let sx = 0; let sy = 0; let sxx = 0; let sxy = 0;
  pts.forEach(([i, v]) => { sx += i; sy += v; sxx += i * i; sxy += i * v; });
  const b = (n * sxy - sx * sy) / (n * sxx - sx * sx || 1);
  const a = (sy - b * sx) / n;
  return arr.map((_, i) => a + b * i);
}

/* ── Sparkline (mini courbe du pied de carte) ─────────────────────────────── */
export function Sparkline({ data, color, w = 96, h = 32 }) {
  const vals = (data || []).filter((v) => Number.isFinite(v));
  if (vals.length < 2) return <svg width={w} height={h} />;
  const pad = 3;
  const min = Math.min(...vals, 0); const max = Math.max(...vals, 0);
  const range = (max - min) || 1;
  const pts = vals.map((v, i) => [pad + i * ((w - 2 * pad) / (vals.length - 1)), h - pad - ((v - min) / range) * (h - 2 * pad)]);
  const d = pts.map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
  const zeroY = h - pad - ((0 - min) / range) * (h - 2 * pad);
  const last = pts[pts.length - 1];
  const area = d + ` L${last[0].toFixed(1)} ${h - pad} L${pad} ${h - pad} Z`;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: 'block' }}>
      <path d={area} fill={color} opacity={0.1} />
      <line x1={pad} x2={w - pad} y1={zeroY} y2={zeroY} stroke="rgba(0,0,0,.07)" strokeWidth={1} />
      <path d={d} fill="none" stroke={color} strokeWidth={1.7} strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={last[0]} cy={last[1]} r={2.4} fill={color} />
    </svg>
  );
}

/* ── Cascade CA → EBITDA (waterfall) ──────────────────────────────────────── */
export function Waterfall({ steps, div = 1 }) {
  if (!Array.isArray(steps) || !steps.length) return null;
  const W = 920; const H = 392; const L = 12; const R = 12; const T = 30; const B = 62;
  const plotW = W - L - R; const plotH = H - T - B;
  const maxV = Math.max(...steps.map((s) => Math.abs(s.val)), 1);
  const y = (v) => T + plotH - (v / maxV) * plotH;
  const n = steps.length; const slot = plotW / n; const bw = slot * 0.56;
  /* Couleurs (charte, revue 2026-08-16) : le terracotta = ALERTE — il ne
     colore plus les charges ordinaires. Charges = ardoise claire (aplat
     accent-soft), produits/CA = sage, sous-totaux = ardoise pleine ; le
     terracotta ne reste que pour un SOUS-TOTAL négatif (la vraie alerte). */
  const SAGE = 'var(--color-sage)'; const TERRA = 'var(--color-terracotta)'; const SLATE = 'var(--color-accent)';
  const SOFT = 'var(--color-accent-soft)';
  const e = []; const zeroY = y(0);
  e.push(<line key="base" x1={L} x2={W - R} y1={zeroY} y2={zeroY} stroke={GRID} strokeWidth={1} />);
  let running = 0; const endY = [];
  steps.forEach((st, i) => {
    const cx = L + (i + 0.5) * slot;
    let top; let bot; let color; let vcolor;
    if (st.kind === 'delta') {
      const from = running; const to = running + st.val;
      top = y(Math.max(from, to)); bot = y(Math.min(from, to));
      color = st.val < 0 ? SOFT : SAGE; running = to;
      vcolor = st.val < 0 ? MUTED : SAGE;
    } else {
      top = y(st.val); bot = zeroY;
      color = st.kind === 'start' ? SAGE : (st.val < 0 ? TERRA : SLATE);
      running = st.val;
      vcolor = st.val < 0 ? TERRA : INK;
    }
    endY.push(y(running));
    e.push(<rect key={`b${i}`} x={cx - bw / 2} y={top} width={bw} height={Math.max(2, bot - top)} rx={2} fill={color} />);
    e.push(<text key={`v${i}`} x={cx} y={top - 6} textAnchor="middle" fontSize={12.5} fontWeight={700} fill={vcolor} fontFamily={F}>{fmtNum(st.val, div)}</text>);
    const words = st.label.split(' ');
    e.push(
      <text key={`l${i}`} x={cx} y={H - B + 22} textAnchor="middle" fontSize={14} fontWeight={st.kind === 'delta' ? 500 : 700} fill={st.kind === 'delta' ? MUTED : 'var(--color-text)'} fontFamily={F}>
        {words.map((w, wi) => <tspan key={wi} x={cx} dy={wi === 0 ? 0 : 15}>{w}</tspan>)}
      </text>,
    );
  });
  for (let i = 0; i < n - 1; i++) {
    e.push(<line key={`cn${i}`} x1={L + (i + 0.5) * slot + bw / 2} x2={L + (i + 1.5) * slot - bw / 2} y1={endY[i]} y2={endY[i]} stroke={MUTED} strokeWidth={1} strokeDasharray="3 3" />);
  }
  return <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block', height: 'auto' }}>{e}</svg>;
}

/* ── Revenus & charges op. — 12 mois glissants (ciseaux) ──────────────────── */
export function Scissors({ rolling, div = 1 }) {
  const pts = (rolling || []).filter((p) => Number.isFinite(p.rev) && Number.isFinite(p.op));
  if (pts.length < 2) return <Empty />;
  const rev = pts.map((p) => p.rev); const chg = pts.map((p) => p.op); const labs = pts.map((p) => p.month);
  const W = 720; const H = 300; const L = 46; const R = 16; const T = 24; const B = 46;
  const plotW = W - L - R; const plotH = H - T - B; const n = pts.length; const slot = plotW / (n - 1);
  const all = rev.concat(chg);
  let ymax = Math.max(...all); let ymin = Math.min(...all);
  const pad = (ymax - ymin) * 0.35 || 500; ymax += pad; ymin -= pad;
  const x = (i) => L + i * slot; const y = (v) => T + plotH - ((v - ymin) / (ymax - ymin)) * plotH;
  const e = [];
  const step = niceStep((ymax - ymin) / 4);
  const gs = Math.ceil(ymin / step) * step; const ge = Math.floor(ymax / step) * step;
  for (let g = gs; g <= ge; g += step) {
    e.push(<line key={`g${g}`} x1={L} x2={W - R} y1={y(g)} y2={y(g)} stroke={GRID_SOFT} strokeWidth={1} />);
    e.push(<text key={`gl${g}`} x={L - 8} y={y(g) + 3.5} textAnchor="end" fontSize={12} fill={MUTED} fontFamily={F}>{fmtNum(g, div)}</text>);
  }
  // Distinction non-chromatique (daltoniens) : Revenus = ligne sage AVEC points
  // ronds ; Charges op. = ligne terracotta sans point. Couleur + forme.
  const mkLine = (arr, color, key, dots) => {
    const p = arr.map((v, i) => [x(i), y(v)]);
    e.push(<path key={key} d={p.map((q, i) => (i ? 'L' : 'M') + q[0].toFixed(1) + ' ' + q[1].toFixed(1)).join(' ')} fill="none" stroke={color} strokeWidth={2.6} strokeLinejoin="round" strokeLinecap="round" />);
    if (dots) p.forEach((q, i) => e.push(<circle key={`${key}d${i}`} cx={q[0]} cy={q[1]} r={2.6} fill={color} />));
  };
  mkLine(rev, 'var(--color-sage)', 'rev', true); mkLine(chg, 'var(--color-terracotta)', 'chg', false);
  labs.forEach((lb, i) => { if (i % 2 === 0 || i === n - 1) e.push(<text key={`x${i}`} x={x(i)} y={H - B + 18} textAnchor="middle" fontSize={11.5} fill={MUTED} fontFamily={F}>{lb}</text>); });
  e.push(<line key="lr" x1={L} x2={L + 20} y1={10} y2={10} stroke="var(--color-sage)" strokeWidth={2.6} strokeLinecap="round" />);
  e.push(<circle key="lrd" cx={L + 10} cy={10} r={2.6} fill="var(--color-sage)" />);
  e.push(<text key="lrt" x={L + 25} y={14} fontSize={12.5} fill="var(--color-text)" fontFamily={F}>{i18next.t('phrases:p_a08a099a37')}</text>);
  e.push(<line key="lc" x1={L + 100} x2={L + 120} y1={10} y2={10} stroke="var(--color-terracotta)" strokeWidth={2.6} strokeLinecap="round" />);
  e.push(<text key="lct" x={L + 125} y={14} fontSize={12.5} fill="var(--color-text)" fontFamily={F}>{i18next.t('phrases:p_2847e9bf8c')}</text>);
  return <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block', height: 'auto' }}>{e}</svg>;
}

/* ── Suivi de la liquidité — composition mensuelle (barres signées + net) ──── */
export function LiqTrack({ stack, persoLine, labels, div = 1 }) {
  const rawUid = React.useId();
  const uid = rawUid.replace(/[^a-zA-Z0-9]/g, '');
  const comps = (stack || []).filter((c) => Array.isArray(c.m));
  if (!comps.length) return <Empty />;
  // Trames SVG : créances = hachuré blanc/bleu · stocks = trame noir/blanc ·
  // acomptes = couleur + hachure blanche.
  const HB = `hb${uid}`; const BW = `bw${uid}`;
  const patDefs = [];
  const fillMap = new Map();
  comps.forEach((c, ci) => {
    if (c.fillType === 'hatch') fillMap.set(c, `url(#${HB})`);
    else if (c.fillType === 'dots') fillMap.set(c, `url(#${BW})`);
    else if (c.fillType === 'hatchWhite') {
      const id = `wh${uid}_${ci}`;
      patDefs.push(
        <pattern key={id} id={id} width={6} height={6} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width={6} height={6} fill={c.color} />
          <line x1={0} y1={0} x2={0} y2={6} stroke="#FFFFFF" strokeWidth={2.2} />
        </pattern>,
      );
      fillMap.set(c, `url(#${id})`);
    } else fillMap.set(c, c.color);
  });
  const fillOf = (c) => fillMap.get(c) || c.color;
  const pos = comps.filter((c) => !c.neg); const neg = comps.filter((c) => c.neg);
  const W = 760; const H = 430; const L = 46; const R = 14; const T = 20; const B = 104;
  const plotW = W - L - R; const plotH = H - T - B; const n = 12; const slot = plotW / n;
  const net = [];
  for (let i = 0; i < n; i++) net.push(pos.reduce((s, c) => s + (c.m[i] || 0), 0) - neg.reduce((s, c) => s + (c.m[i] || 0), 0));
  let ymax = 0; let ymin = 0;
  for (let i = 0; i < n; i++) {
    ymax = Math.max(ymax, pos.reduce((s, c) => s + (c.m[i] || 0), 0), net[i]);
    ymin = Math.min(ymin, -neg.reduce((s, c) => s + (c.m[i] || 0), 0));
  }
  const sp = (ymax - ymin) || 1; ymax += sp * 0.08; ymin -= sp * 0.06;
  const x = (i) => L + (i + 0.5) * slot; const y = (v) => T + plotH - ((v - ymin) / (ymax - ymin)) * plotH;
  const bw = slot * 0.5; const e = [];
  e.push(
    <defs key="pat">
      <pattern id={HB} width={6} height={6} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <rect width={6} height={6} fill="#FFFFFF" />
        <line x1={0} y1={0} x2={0} y2={6} stroke="var(--color-accent)" strokeWidth={2.4} />
      </pattern>
      <pattern id={BW} width={5} height={5} patternUnits="userSpaceOnUse">
        <rect width={5} height={5} fill="#FFFFFF" />
        <circle cx={2.5} cy={2.5} r={1.15} fill="var(--color-text)" />
      </pattern>
      {patDefs}
    </defs>,
  );
  const step = niceStep((ymax - ymin) / 4);
  for (let g = Math.ceil(ymin / step) * step; g <= ymax; g += step) {
    const isZero = Math.abs(g) < 1e-6;
    e.push(<line key={`g${g}`} x1={L} x2={W - R} y1={y(g)} y2={y(g)} stroke={isZero ? 'var(--color-text)' : GRID_SOFT} strokeWidth={isZero ? 1.4 : 1} />);
    e.push(<text key={`gl${g}`} x={L - 8} y={y(g) + 3.5} textAnchor="end" fontSize={11} fill={MUTED} fontFamily={F}>{fmtNum(g, div)}</text>);
  }
  for (let i = 0; i < n; i++) {
    const cx = x(i); let acc = 0;
    pos.forEach((c, ci) => { const v = c.m[i] || 0; if (v <= 0) return; const y0 = y(acc); const y1 = y(acc + v); e.push(<rect key={`p${i}_${ci}`} x={cx - bw / 2} y={y1} width={bw} height={Math.max(0.5, y0 - y1)} fill={fillOf(c)} stroke="var(--color-border)" strokeWidth={0.6} />); acc += v; });
    let accN = 0;
    neg.forEach((c, ci) => { const v = c.m[i] || 0; if (v <= 0) return; const y0 = y(-accN); const y1 = y(-accN - v); e.push(<rect key={`ng${i}_${ci}`} x={cx - bw / 2} y={y0} width={bw} height={Math.max(0.5, y1 - y0)} fill={fillOf(c)} stroke="var(--color-border)" strokeWidth={0.6} />); accN += v; });
  }
  if (Array.isArray(persoLine)) {
    const pp = persoLine.map((v, i) => [x(i), y(Math.abs(v || 0))]);
    e.push(<path key="perso" d={pp.map((q, i) => (i ? 'L' : 'M') + q[0].toFixed(1) + ' ' + q[1].toFixed(1)).join(' ')} fill="none" stroke="var(--color-terracotta)" strokeWidth={1.8} strokeDasharray="4 3" strokeLinejoin="round" />);
  }
  const np = net.map((v, i) => [x(i), y(v)]);
  e.push(<path key="net" d={np.map((q, i) => (i ? 'L' : 'M') + q[0].toFixed(1) + ' ' + q[1].toFixed(1)).join(' ')} fill="none" stroke={INK} strokeWidth={2.8} strokeLinejoin="round" strokeLinecap="round" />);
  np.forEach((q, i) => {
    e.push(<circle key={`nc${i}`} cx={q[0]} cy={q[1]} r={2.6} fill={INK} />);
    // Valeur (en k) au-dessus du point, halo blanc pour la lisibilité sur les barres.
    e.push(<text key={`nt${i}`} x={q[0]} y={q[1] - 9} textAnchor="middle" fontSize={11} fontWeight={700} fill={INK} stroke="#FFFFFF" strokeWidth={2.8} paintOrder="stroke" fontFamily={F}>{fmtNum(net[i], div)}</text>);
  });
  const monthLbls = (Array.isArray(labels) && labels.length === 12) ? labels : Array.from({ length: 12 }, (_, i) => i18next.t(`common:months.${i + 1}`));
  monthLbls.forEach((lb, i) => e.push(<text key={`ml${i}`} x={x(i)} y={T + plotH + 16} textAnchor="middle" fontSize={10.5} fill={MUTED} fontFamily={F}>{lb}</text>));
  // Légende — rangée 1 : barres.
  const ly1 = T + plotH + 40; let lx = L;
  comps.forEach((c, ci) => {
    e.push(<rect key={`lg${ci}`} x={lx} y={ly1 - 9} width={13} height={11} rx={2} fill={fillOf(c)} stroke="var(--color-border)" strokeWidth={0.6} />);
    e.push(<text key={`lgt${ci}`} x={lx + 17} y={ly1} fontSize={12} fill="var(--color-text)" fontFamily={F}>{c.label}</text>);
    lx += 30 + c.label.length * 6.4;
  });
  // Rangée 2 : courbes — la courbe nette est mise en évidence (plus grande, en gras).
  const ly2 = T + plotH + 64; let lx2 = L;
  const netLbl = i18next.t('phrases:p_15eb4471d5');
  e.push(<line key="lgn" x1={lx2} x2={lx2 + 28} y1={ly2 - 4} y2={ly2 - 4} stroke={INK} strokeWidth={3.2} strokeLinecap="round" />);
  e.push(<text key="lgnt" x={lx2 + 34} y={ly2} fontSize={13.5} fontWeight={700} fill={INK} fontFamily={F}>{netLbl}</text>);
  lx2 += 34 + netLbl.length * 7.1 + 22;
  if (Array.isArray(persoLine)) {
    e.push(<line key="lgp" x1={lx2} x2={lx2 + 24} y1={ly2 - 4} y2={ly2 - 4} stroke="var(--color-terracotta)" strokeWidth={1.8} strokeDasharray="4 3" strokeLinecap="round" />);
    e.push(<text key="lgpt" x={lx2 + 30} y={ly2} fontSize={12} fill="var(--color-text)" fontFamily={F}>{i18next.t('phrases:p_2924c57923')}</text>);
  }
  return <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block', height: 'auto' }}>{e}</svg>;
}

/* ── Graphique de drawer, multi-mode ──────────────────────────────────────── */
export function KpiChart({ kpi, color, div = 1 }) {
  const mode = kpi.mode || 'level';
  if (mode === 'pills') return <Pills kpi={kpi} />;
  if (mode === 'liqproj') return <LiqProj kpi={kpi} color={color} div={div} />;
  if (mode === 'runway') return <Runway kpi={kpi} color={color} div={div} />;
  if (mode === 'stack') return <LiqTrack stack={kpi.stack} labels={kpi.stackLabels} div={div} />;
  if (mode === 'flow') return <Flow kpi={kpi} color={color} div={div} />;
  if (mode === 'ebitda') return <EbitdaBars kpi={kpi} div={div} />;
  if (mode === 'caept') return <CaEptChart kpi={kpi} />;
  if (mode === 'vaept') return <VaEptChart kpi={kpi} />;
  if (mode === 'rendbars') return <RendBars kpi={kpi} div={div} />;
  if (mode === 'burnbars') return <BurnBars kpi={kpi} div={div} />;
  if (mode === 'scissors') return <Scissors rolling={kpi.rolling} div={div} />;
  return <Level kpi={kpi} color={color} div={div} mode={mode} />;
}

const CW = 424; const CH = 214; const CL = 6; const CR = 6; const CT = 30; const CB = 32;
const cPlotW = CW - CL - CR; const cPlotH = CH - CT - CB; const cSlot = cPlotW / 12; const cBottom = CT + cPlotH;
const MONTHS_S = () => Array.from({ length: 12 }, (_, i) => i18next.t(`common:months.${i + 1}`).toLowerCase());

function Empty() {
  return <div style={{ padding: '18px 0', fontSize: 12.5, color: 'var(--color-text-muted)' }}>{i18next.t('phrases:p_4b01c427b3')}</div>;
}

function Flow({ kpi, color, div }) {
    const { t } = useTranslation();
const m25 = kpi.m2025 || null; const m26 = kpi.m2026 || [];
  const actualN = kpi.actualN != null ? kpi.actualN : m26.length;
  const has25 = Array.isArray(m25) && m25.some((v) => Number.isFinite(v));
  const all = (m25 || []).concat(m26).filter((v) => Number.isFinite(v));
  if (!m26.some((v) => Number.isFinite(v))) return <Empty />;
  // Moyenne MOBILE 12 mois glissants : fenêtre des 12 derniers mois ancrée sur la
  // série 24 mois (N-1 → N). Ex. janvier N = fév N-1 … janv N. À défaut de N-1,
  // fenêtre croissante en début d'année.
  const series24 = (has25 ? m25 : Array(12).fill(null)).concat(m26);
  const roll = [];
  for (let i = 0; i < 12; i++) {
    const end = 12 + i;
    const w = series24.slice(end - 11, end + 1).filter((v) => Number.isFinite(v));
    roll.push(w.length ? w.reduce((s, v) => s + v, 0) / w.length : null);
  }
  const vals = all.concat(roll.filter((v) => Number.isFinite(v)));
  let ymin = Math.min(0, ...vals); let ymax = Math.max(0, ...vals); const sp = (ymax - ymin) || 1; ymax += sp * 0.12; if (ymin < 0) ymin -= sp * 0.12;
  const y = (v) => CT + cPlotH - ((v - ymin) / (ymax - ymin)) * cPlotH;
  const zeroY = y(0); const gw = cSlot * 0.30; const gpad = cSlot * 0.06; const e = [];
  // Hachures pour les mois PROJETÉS (distinction non-chromatique du réel).
  const projId = `projhatch_${kpi.key}`;
  e.push(
    <defs key="fdefs"><pattern id={projId} width={5} height={5} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
      <rect width={5} height={5} fill={color} opacity={0.16} />
      <line x1={0} y1={0} x2={0} y2={5} stroke={color} strokeWidth={1.8} />
    </pattern></defs>,
  );
  e.push(<line key="z" x1={CL} x2={CW - CR} y1={zeroY} y2={zeroY} stroke={GRID} strokeWidth={1} />);
  for (let i = 0; i < 12; i++) {
    const cx = CL + (i + 0.5) * cSlot;
    if (has25 && Number.isFinite(m25[i])) { const vy = y(m25[i]); e.push(<rect key={`p${i}`} x={cx - gw - gpad / 2} y={Math.min(vy, zeroY)} width={gw} height={Math.max(1, Math.abs(vy - zeroY))} rx={1.5} fill="var(--color-accent-soft)" />); }
    if (Number.isFinite(m26[i])) { const vy = y(m26[i]); const proj = i >= actualN; e.push(<rect key={`c${i}`} x={has25 ? cx + gpad / 2 : cx - gw / 2} y={Math.min(vy, zeroY)} width={gw} height={Math.max(1, Math.abs(vy - zeroY))} rx={1.5} fill={proj ? `url(#${projId})` : color} stroke={proj ? color : 'none'} strokeWidth={proj ? 0.6 : 0} />); }
  }
  const rp = roll.map((v, i) => (Number.isFinite(v) ? [CL + (i + 0.5) * cSlot, y(v)] : null)).filter(Boolean);
  if (rp.length >= 2) {
    e.push(<path key="roll" d={rp.map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ')} fill="none" stroke={INK} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />);
  }
  MONTHS_S().forEach((lb, i) => e.push(<text key={`ml${i}`} x={CL + (i + 0.5) * cSlot} y={cBottom + 17} textAnchor="middle" fontSize={10.5} fill={MUTED} fontFamily={F}>{lb}</text>));
  let lx = CL;
  if (has25) { e.push(<rect key="lg1" x={lx} y={8} width={11} height={9} rx={2} fill="var(--color-accent-soft)" />); e.push(<text key="lg1t" x={lx + 15} y={16} fontSize={12} fill={MUTED} fontFamily={F}>N-1</text>); lx += 46; }
  e.push(<rect key="lg2" x={lx} y={8} width={11} height={9} rx={2} fill={color} />); e.push(<text key="lg2t" x={lx + 15} y={16} fontSize={12} fill={MUTED} fontFamily={F}>{t('phrases:p_963e56fe84')}</text>); lx += 52;
  e.push(<rect key="lg3" x={lx} y={8} width={11} height={9} rx={2} fill={`url(#${projId})`} stroke={color} strokeWidth={0.6} />); e.push(<text key="lg3t" x={lx + 15} y={16} fontSize={12} fill={MUTED} fontFamily={F}>{i18next.t('phrases:p_8aa49fe840')}</text>); lx += 88;
  e.push(<line key="lg4" x1={lx} x2={lx + 20} y1={12.5} y2={12.5} stroke={INK} strokeWidth={2} strokeLinecap="round" />); e.push(<text key="lg4t" x={lx + 24} y={16} fontSize={12} fill={MUTED} fontFamily={F}>{i18next.t('phrases:p_cf2784d55e')}</text>);
  return <svg viewBox={`0 0 ${CW} ${CH}`} width="100%" style={{ display: 'block', height: 'auto' }}>{e}</svg>;
}

function Level({ kpi, color, div, mode }) {
  let series;
  if (mode === 'rate') {
    const ca = kpi.caMonthly || []; const self = kpi.series || [];
    series = self.map((v, i) => (Number.isFinite(v) && ca[i] ? Math.round((v / ca[i]) * 1000) / 10 : null));
  } else { series = (kpi.series || []).map((v) => (Number.isFinite(v) ? v : null)); }
  const present = series.filter((v) => Number.isFinite(v));
  if (present.length < 2) return <Empty />;
  const trend = trendLine(series);
  const vals = present.concat(trend.filter((v) => Number.isFinite(v)));
  let ymin = Math.min(...vals); let ymax = Math.max(...vals);
  if (mode === 'rate') { ymin = Math.min(0, ymin); ymax = Math.max(0, ymax); }
  if (kpi.threshold) { ymax = Math.max(ymax, kpi.threshold.value); ymin = Math.min(ymin, kpi.threshold.value); }
  const sp = (ymax - ymin) || 1; ymax += sp * 0.14; ymin -= sp * 0.14;
  const y = (v) => CT + cPlotH - ((v - ymin) / (ymax - ymin)) * cPlotH;
  const gid = `g_${kpi.key}`; const e = [];
  const pts = series.map((v, i) => (Number.isFinite(v) ? [CL + (i + 0.5) * cSlot, y(v)] : null));
  const line = pts.filter(Boolean).map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
  const firstP = pts.find(Boolean); const lastP = [...pts].reverse().find(Boolean);
  if (ymin < 0 && ymax > 0) e.push(<line key="z" x1={CL} x2={CW - CR} y1={y(0)} y2={y(0)} stroke={GRID} strokeWidth={1} />);
  e.push(
    <defs key="df"><linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor={color} stopOpacity={0.24} /><stop offset="100%" stopColor={color} stopOpacity={0} />
    </linearGradient></defs>,
  );
  e.push(<path key="ar" d={`${line} L${lastP[0].toFixed(1)} ${cBottom} L${firstP[0].toFixed(1)} ${cBottom} Z`} fill={`url(#${gid})`} />);
  const t0 = trend.find((v) => Number.isFinite(v)); const t1 = [...trend].reverse().find((v) => Number.isFinite(v));
  e.push(<line key="tr" x1={firstP[0]} y1={y(t0)} x2={lastP[0]} y2={y(t1)} stroke={color} strokeWidth={1.6} strokeDasharray="0.5 4" opacity={0.75} strokeLinecap="round" />);
  const aN = (mode === 'rate' && Number.isFinite(kpi.actualN)) ? kpi.actualN : 12;
  const isProj = (i) => i >= aN;
  if (mode === 'rate' && aN < 12) {
    // Réel : trait plein. Projection : même couleur, atténuée (pas de tiret pour
    // ne pas confondre avec la tendance, elle en tiret).
    const realP = pts.filter((p, i) => p && i < aN);
    const projStart = Math.max(0, aN - 1);
    const projP = pts.filter((p, i) => p && i >= projStart);
    if (realP.length >= 2) e.push(<path key="mlr" d={realP.map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ')} fill="none" stroke={color} strokeWidth={2.4} strokeLinejoin="round" strokeLinecap="round" />);
    if (projP.length >= 2) e.push(<path key="mlp" d={projP.map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ')} fill="none" stroke={color} strokeWidth={1.8} strokeOpacity={0.4} strokeLinejoin="round" strokeLinecap="round" />);
  } else {
    e.push(<path key="ml" d={line} fill="none" stroke={color} strokeWidth={2.4} strokeLinejoin="round" strokeLinecap="round" />);
  }
  pts.forEach((p, i) => p && e.push(<circle key={`c${i}`} cx={p[0]} cy={p[1]} r={2} fill={isProj(i) ? '#fff' : color} stroke={color} strokeWidth={isProj(i) ? 1 : 0} />));
  if (mode === 'rate') series.forEach((v, i) => Number.isFinite(v) && e.push(<text key={`vl${i}`} x={pts[i][0]} y={Math.max(24, pts[i][1] - 7)} textAnchor="middle" fontSize={10.5} fontWeight={600} fill={isProj(i) ? MUTED : 'var(--color-text)'} fontFamily={F}>{Math.round(v)}%</text>));
  if (kpi.threshold) {
    const ty = y(kpi.threshold.value);
    e.push(<line key="th" x1={CL} x2={CW - CR} y1={ty} y2={ty} stroke="var(--color-terracotta)" strokeWidth={1.3} strokeDasharray="6 3" opacity={0.9} />);
    e.push(<text key="tht" x={CL + 2} y={ty - 4} fontSize={10.5} fontWeight={700} fill="var(--color-terracotta)" fontFamily={F}>{kpi.threshold.label}</text>);
  }
  MONTHS_S().forEach((lb, i) => e.push(<text key={`ml${i}`} x={CL + (i + 0.5) * cSlot} y={cBottom + 17} textAnchor="middle" fontSize={10.5} fill={MUTED} fontFamily={F}>{lb}</text>));
  const lbl1 = mode === 'rate' ? i18next.t('phrases:p_be457b933d') : i18next.t('phrases:p_272c1b22fe');
  e.push(<line key="lg1" x1={CL} x2={CL + 18} y1={12.5} y2={12.5} stroke={color} strokeWidth={2.2} strokeLinecap="round" />);
  e.push(<text key="lg1t" x={CL + 23} y={15.5} fontSize={11} fill={MUTED} fontFamily={F}>{lbl1}</text>);
  let x2 = CL + 30 + Math.round(lbl1.length * 6.6);
  if (mode === 'rate' && aN < 12) {
    e.push(<line key="lgp" x1={x2} x2={x2 + 18} y1={12.5} y2={12.5} stroke={color} strokeWidth={1.8} strokeOpacity={0.4} strokeLinecap="round" />);
    e.push(<text key="lgpt" x={x2 + 23} y={15.5} fontSize={11} fill={MUTED} fontFamily={F}>{i18next.t('phrases:p_8aa49fe840')}</text>);
    x2 += 92;
  }
  e.push(<line key="lg2" x1={x2} x2={x2 + 18} y1={12.5} y2={12.5} stroke={color} strokeWidth={1.6} strokeDasharray="0.5 4" opacity={0.75} strokeLinecap="round" />);
  e.push(<text key="lg2t" x={x2 + 23} y={15.5} fontSize={11} fill={MUTED} fontFamily={F}>{i18next.t('phrases:p_add2e69302')}</text>);
  return <svg viewBox={`0 0 ${CW} ${CH}`} width="100%" style={{ display: 'block', height: 'auto' }}>{e}</svg>;
}

function LiqProj({ kpi, color, div }) {
    const { t } = useTranslation();
const p = kpi.lp || {}; const reel = (p.reel || []).filter((v) => Number.isFinite(v));
  if (reel.length < 2) return <Empty />;
  const prev = p.prev || []; const n1 = p.n1 || []; const credit = p.credit;
  const start = p.prevStart != null ? p.prevStart : reel.length - 1;
  const allv = reel.concat(prev.filter(Number.isFinite), (n1 || []).filter(Number.isFinite), credit != null ? [credit] : []);
  let ymin = Math.min(...allv); let ymax = Math.max(...allv); const sp = (ymax - ymin) || 1; ymax += sp * 0.1; ymin -= sp * 0.1;
  const xAt = (i) => CL + (i + 0.5) * cSlot;
  const y = (v) => CT + cPlotH - ((v - ymin) / (ymax - ymin)) * cPlotH;
  const e = []; const hid = `hatch_${kpi.key}`;
  if (credit != null) {
    const zTop = y(0); const zBot = y(credit);
    e.push(
      <defs key="hd"><pattern id={hid} width={7} height={7} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <rect width={7} height={7} fill="var(--color-terracotta)" opacity={0.08} />
        <line x1={0} y1={0} x2={0} y2={7} stroke="var(--color-terracotta)" strokeWidth={1.4} opacity={0.4} />
      </pattern></defs>,
    );
    e.push(<rect key="cz" x={CL} y={zTop} width={cPlotW} height={Math.max(0, zBot - zTop)} fill={`url(#${hid})`} />);
    e.push(<line key="cl" x1={CL} x2={CW - CR} y1={zBot} y2={zBot} stroke="var(--color-terracotta)" strokeWidth={1.5} strokeDasharray="6 3" opacity={0.95} />);
    e.push(<text key="clt" x={CL + 4} y={zBot - 5} fontSize={10.5} fontWeight={700} fill="var(--color-terracotta)" fontFamily={F}>{i18next.t('phrases:p_c616874d65')} {fmtNum(credit, div)}</text>);
  }
  if (ymin < 0 && ymax > 0) e.push(<line key="z" x1={CL} x2={CW - CR} y1={y(0)} y2={y(0)} stroke={GRID} strokeWidth={1} />);
  if (Array.isArray(n1) && n1.some(Number.isFinite)) {
    const np = n1.map((v, i) => (Number.isFinite(v) ? [xAt(i), y(v)] : null)).filter(Boolean);
    e.push(<path key="n1" d={np.map((q, i) => (i ? 'L' : 'M') + q[0].toFixed(1) + ' ' + q[1].toFixed(1)).join(' ')} fill="none" stroke={MUTED} strokeWidth={1.6} strokeDasharray="5 4" strokeLinejoin="round" />);
  }
  const rp = reel.map((v, i) => [xAt(i), y(v)]);
  e.push(<path key="reel" d={rp.map((q, i) => (i ? 'L' : 'M') + q[0].toFixed(1) + ' ' + q[1].toFixed(1)).join(' ')} fill="none" stroke={INK} strokeWidth={2.6} strokeLinejoin="round" strokeLinecap="round" />);
  rp.forEach((q, i) => e.push(<circle key={`rd${i}`} cx={q[0]} cy={q[1]} r={2.4} fill={INK} />));
  if (prev.length >= 2) {
    const pp = prev.map((v, i) => [xAt(start + i), y(v)]);
    e.push(<path key="prev" d={pp.map((q, i) => (i ? 'L' : 'M') + q[0].toFixed(1) + ' ' + q[1].toFixed(1)).join(' ')} fill="none" stroke={color} strokeWidth={2.2} strokeDasharray="2 3" strokeLinejoin="round" strokeLinecap="round" />);
    pp.forEach((q, i) => e.push(<circle key={`pd${i}`} cx={q[0]} cy={q[1]} r={2} fill={color} />));
  }
  MONTHS_S().forEach((lb, i) => e.push(<text key={`ml${i}`} x={xAt(i)} y={cBottom + 17} textAnchor="middle" fontSize={10.5} fill={MUTED} fontFamily={F}>{lb}</text>));
  e.push(<line key="l1" x1={CL} x2={CL + 20} y1={12.5} y2={12.5} stroke={INK} strokeWidth={2.4} strokeLinecap="round" />);
  e.push(<text key="l1t" x={CL + 25} y={16} fontSize={11.5} fill={MUTED} fontFamily={F}>{t('phrases:p_963e56fe84')}</text>);
  e.push(<line key="l2" x1={CL + 52} x2={CL + 72} y1={12.5} y2={12.5} stroke={color} strokeWidth={2.2} strokeDasharray="2 3" strokeLinecap="round" />);
  e.push(<text key="l2t" x={CL + 77} y={16} fontSize={11.5} fill={MUTED} fontFamily={F}>{t('phrases:p_0995a40774')}</text>);
  if (Array.isArray(n1) && n1.some(Number.isFinite)) { e.push(<line key="l3" x1={CL + 168} x2={CL + 188} y1={12.5} y2={12.5} stroke={MUTED} strokeWidth={1.6} strokeDasharray="5 4" strokeLinecap="round" />); e.push(<text key="l3t" x={CL + 193} y={16} fontSize={11.5} fill={MUTED} fontFamily={F}>N-1</text>); }
  return <svg viewBox={`0 0 ${CW} ${CH}`} width="100%" style={{ display: 'block', height: 'auto' }}>{e}</svg>;
}

// EBITDA : barres des valeurs ABSOLUES mensuelles (gain sage / perte terracotta,
// projection en opacité réduite) + courbe du cumul sur un 2e axe (INK).
function EbitdaBars({ kpi }) {
  const arr = (kpi.months || []).map((v) => (Number.isFinite(v) ? v : null));
  const actualN = kpi.actualN != null ? kpi.actualN : 12;
  if (!arr.some((v) => Number.isFinite(v))) return <Empty />;
  const cum = []; let acc = 0; let any = false;
  for (let i = 0; i < 12; i++) { if (Number.isFinite(arr[i])) { acc += arr[i]; any = true; } cum.push(any ? acc : null); }
  const barV = arr.filter(Number.isFinite);
  let bmin = Math.min(0, ...barV); let bmax = Math.max(0, ...barV); const bsp = (bmax - bmin) || 1; bmax += bsp * 0.12; if (bmin < 0) bmin -= bsp * 0.12;
  const cumV = cum.filter(Number.isFinite);
  let cmin = Math.min(0, ...cumV); let cmax = Math.max(0, ...cumV); const csp = (cmax - cmin) || 1; cmax += csp * 0.12; if (cmin < 0) cmin -= csp * 0.12;
  const yb = (v) => CT + cPlotH - ((v - bmin) / (bmax - bmin)) * cPlotH;
  const yc = (v) => CT + cPlotH - ((v - cmin) / (cmax - cmin)) * cPlotH;
  const zeroYb = yb(0); const bw = cSlot * 0.5; const e = [];
  e.push(<line key="z" x1={CL} x2={CW - CR} y1={zeroYb} y2={zeroYb} stroke={GRID} strokeWidth={1} />);
  for (let i = 0; i < 12; i++) {
    if (!Number.isFinite(arr[i])) continue;
    const cx = CL + (i + 0.5) * cSlot; const vy = yb(arr[i]); const col = arr[i] >= 0 ? 'var(--color-sage)' : 'var(--color-terracotta)';
    e.push(<rect key={`b${i}`} x={cx - bw / 2} y={Math.min(vy, zeroYb)} width={bw} height={Math.max(1, Math.abs(vy - zeroYb))} rx={1.5} fill={col} opacity={i < actualN ? 1 : 0.34} />);
  }
  const cp = cum.map((v, i) => (Number.isFinite(v) ? [CL + (i + 0.5) * cSlot, yc(v)] : null)).filter(Boolean);
  if (cp.length >= 2) { e.push(<path key="cum" d={cp.map((q, i) => (i ? 'L' : 'M') + q[0].toFixed(1) + ' ' + q[1].toFixed(1)).join(' ')} fill="none" stroke={INK} strokeWidth={2.2} strokeLinejoin="round" strokeLinecap="round" />); cp.forEach((q, i) => e.push(<circle key={`cd${i}`} cx={q[0]} cy={q[1]} r={2.2} fill={INK} />)); }
  MONTHS_S().forEach((lb, i) => e.push(<text key={`ml${i}`} x={CL + (i + 0.5) * cSlot} y={cBottom + 17} textAnchor="middle" fontSize={10.5} fill={MUTED} fontFamily={F}>{lb}</text>));
  let lx = CL;
  e.push(<rect key="l1" x={lx} y={8} width={11} height={9} rx={2} fill="var(--color-sage)" />); e.push(<text key="l1t" x={lx + 15} y={16} fontSize={12} fill={MUTED} fontFamily={F}>{i18next.t('phrases:p_319b193e8e')}</text>); lx += 118;
  e.push(<line key="l2" x1={lx} x2={lx + 20} y1={12.5} y2={12.5} stroke={INK} strokeWidth={2.2} strokeLinecap="round" />); e.push(<text key="l2t" x={lx + 24} y={16} fontSize={12} fill={MUTED} fontFamily={F}>{i18next.t('phrases:p_39d4fcc686')}</text>);
  return <svg viewBox={`0 0 ${CW} ${CH}`} width="100%" style={{ display: 'block', height: 'auto' }}>{e}</svg>;
}

// CA net par EPT : courbe CA net 12 mois glissant (sage, axe gauche) + barres EPT
// mensuel (accent-soft, axe droit). Double-axe.
function CaEptChart({ kpi }) {
  const caR = (kpi.caRoll || []).map((v) => (Number.isFinite(v) ? v : null));
  const eptM = (kpi.eptMonths || []).map((v) => (Number.isFinite(v) ? v : null));
  if (!caR.some(Number.isFinite) && !eptM.some(Number.isFinite)) return <Empty />;
  const caV = caR.filter(Number.isFinite); const eptV = eptM.filter(Number.isFinite);
  const cmax = (caV.length ? Math.max(...caV, 0) : 1) * 1.1 || 1;
  const emax = (eptV.length ? Math.max(...eptV, 0) : 1) * 1.15 || 1;
  const yc = (v) => CT + cPlotH - (v / cmax) * cPlotH;
  const ye = (v) => CT + cPlotH - (v / emax) * cPlotH;
  const e = []; const bw = cSlot * 0.44; const zeroE = ye(0);
  for (let i = 0; i < 12; i++) { if (!Number.isFinite(eptM[i])) continue; const cx = CL + (i + 0.5) * cSlot; const vy = ye(eptM[i]); e.push(<rect key={`e${i}`} x={cx - bw / 2} y={vy} width={bw} height={Math.max(1, zeroE - vy)} rx={1.5} fill="var(--color-accent-soft)" />); }
  const cp = caR.map((v, i) => (Number.isFinite(v) ? [CL + (i + 0.5) * cSlot, yc(v)] : null)).filter(Boolean);
  if (cp.length >= 2) { e.push(<path key="ca" d={cp.map((q, i) => (i ? 'L' : 'M') + q[0].toFixed(1) + ' ' + q[1].toFixed(1)).join(' ')} fill="none" stroke="var(--color-sage)" strokeWidth={2.4} strokeLinejoin="round" strokeLinecap="round" />); cp.forEach((q, i) => e.push(<circle key={`cd${i}`} cx={q[0]} cy={q[1]} r={2.2} fill="var(--color-sage)" />)); }
  MONTHS_S().forEach((lb, i) => e.push(<text key={`ml${i}`} x={CL + (i + 0.5) * cSlot} y={cBottom + 17} textAnchor="middle" fontSize={10.5} fill={MUTED} fontFamily={F}>{lb}</text>));
  let lx = CL;
  e.push(<line key="l1" x1={lx} x2={lx + 20} y1={12.5} y2={12.5} stroke="var(--color-sage)" strokeWidth={2.4} strokeLinecap="round" />); e.push(<text key="l1t" x={lx + 24} y={16} fontSize={12} fill={MUTED} fontFamily={F}>{i18next.t('phrases:p_a42a88db8e')}</text>); lx += 168;
  e.push(<rect key="l2" x={lx} y={8} width={11} height={9} rx={2} fill="var(--color-accent-soft)" />); e.push(<text key="l2t" x={lx + 15} y={16} fontSize={12} fill={MUTED} fontFamily={F}>{i18next.t('phrases:p_43836e43f2')}</text>);
  return <svg viewBox={`0 0 ${CW} ${CH}`} width="100%" style={{ display: 'block', height: 'auto' }}>{e}</svg>;
}

// Valeur ajoutée par EPT : productivité = VA 12 mois glissants / effectif moyen.
// Ligne unique (sage) + points + valeurs, en kCHF/EPT. Insensible au mix marchandises.
function VaEptChart({ kpi }) {
  const roll = (kpi.vaEptRoll || []).map((v) => (Number.isFinite(v) ? v : null));
  const vals = roll.filter(Number.isFinite);
  if (vals.length < 2) return <Empty />;
  const vmax = Math.max(...vals, 0) * 1.15 || 1;
  const yv = (v) => CT + cPlotH - (v / vmax) * cPlotH;
  const e = [];
  const pts = roll.map((v, i) => (Number.isFinite(v) ? [CL + (i + 0.5) * cSlot, yv(v)] : null)).filter(Boolean);
  e.push(<path key="area" d={`M${pts[0][0].toFixed(1)} ${yv(0).toFixed(1)} ` + pts.map((q) => `L${q[0].toFixed(1)} ${q[1].toFixed(1)}`).join(' ') + ` L${pts[pts.length - 1][0].toFixed(1)} ${yv(0).toFixed(1)} Z`} fill="color-mix(in srgb, var(--color-sage) 12%, transparent)" />);
  e.push(<path key="va" d={pts.map((q, i) => (i ? 'L' : 'M') + q[0].toFixed(1) + ' ' + q[1].toFixed(1)).join(' ')} fill="none" stroke="var(--color-sage)" strokeWidth={2.4} strokeLinejoin="round" strokeLinecap="round" />);
  roll.forEach((v, i) => { if (!Number.isFinite(v)) return; const cx = CL + (i + 0.5) * cSlot; const cy = yv(v); e.push(<circle key={`d${i}`} cx={cx} cy={cy} r={2.2} fill="var(--color-sage)" />); e.push(<text key={`t${i}`} x={cx} y={cy - 7} textAnchor="middle" fontSize={10.5} fontWeight={600} fill="var(--color-navy)" fontFamily={F}>{Math.round(v / 1000)}</text>); });
  MONTHS_S().forEach((lb, i) => e.push(<text key={`ml${i}`} x={CL + (i + 0.5) * cSlot} y={cBottom + 17} textAnchor="middle" fontSize={10.5} fill={MUTED} fontFamily={F}>{lb}</text>));
  e.push(<line key="l1" x1={CL} x2={CL + 20} y1={12.5} y2={12.5} stroke="var(--color-sage)" strokeWidth={2.4} strokeLinecap="round" />);
  e.push(<text key="l1t" x={CL + 24} y={16} fontSize={12} fill={MUTED} fontFamily={F}>{i18next.t('phrases:p_b29b1789a4')}</text>);
  return <svg viewBox={`0 0 ${CW} ${CH}`} width="100%" style={{ display: 'block', height: 'auto' }}>{e}</svg>;
}

// Burn rate : évolution de la charge mensuelle fixe — barres N-2 / N-1 / N
// (N-1 et N-2 = charges effectives annuelles ÷ 12). Année courante en navy.
function BurnBars({ kpi, div }) {
  const bars = (kpi.burnBars || []).filter((b) => Number.isFinite(b.val));
  if (bars.length < 1) return <Empty />;
  const vmax = Math.max(...bars.map((b) => b.val), 0) * 1.2 || 1;
  const y = (v) => CT + cPlotH - (v / vmax) * cPlotH; const zeroY = y(0);
  const e = []; const gw = cPlotW / bars.length; const bw = Math.min(64, gw * 0.5);
  bars.forEach((b, i) => {
    const gx = CL + gw * (i + 0.5); const vy = y(b.val); const last = i === bars.length - 1;
    e.push(<rect key={`b${i}`} x={gx - bw / 2} y={vy} width={bw} height={Math.max(1, zeroY - vy)} rx={2} fill={last ? 'var(--color-navy)' : 'var(--color-accent-soft)'} />);
    e.push(<text key={`v${i}`} x={gx} y={vy - 6} textAnchor="middle" fontSize={11.5} fontWeight={700} fill="var(--color-navy)" fontFamily={F}>{fmtNum(b.val, div)}</text>);
    e.push(<text key={`l${i}`} x={gx} y={cBottom + 17} textAnchor="middle" fontSize={12} fill={MUTED} fontFamily={F}>{b.label}</text>);
  });
  e.push(<text key="cap" x={CL} y={16} fontSize={11.5} fill={MUTED} fontFamily={F}>{i18next.t('phrases:p_ee8d741d1f')}</text>);
  return <svg viewBox={`0 0 ${CW} ${CH}`} width="100%" style={{ display: 'block', height: 'auto' }}>{e}</svg>;
}

// Rendement masse salariale : barres comparatives N vs N-1 pour CA/EPT et Coût/EPT,
// + rappel du ratio de rendement (actuel · 2025).
function RendBars({ kpi, div }) {
    const { t } = useTranslation();
const c = kpi.rendCmp || {};
  const groups = [
    { label: c.label1 || t('phrases:p_b34bba20b1'), cur: c.caEpt, prev: c.caEptN1 },
    { label: t('phrases:p_1943c277cd'), cur: c.coutEpt, prev: c.coutEptN1 },
  ];
  const allV = groups.flatMap((g) => [g.cur, g.prev]).filter(Number.isFinite);
  if (!allV.length) return <Empty />;
  const vmax = Math.max(...allV, 0) * 1.18 || 1;
  const y = (v) => CT + cPlotH - (v / vmax) * cPlotH; const zeroY = y(0);
  const e = []; const gwidth = cPlotW / groups.length; const bw = gwidth * 0.24;
  groups.forEach((g, gi) => {
    const gx = CL + gwidth * (gi + 0.5);
    [['cur', 'var(--color-navy)', -1], ['prev', 'var(--color-accent-soft)', 1]].forEach(([k, col, side]) => {
      const v = g[k]; if (!Number.isFinite(v)) return; const bx = gx + side * (bw * 0.55 + 2) - bw / 2; const vy = y(v);
      e.push(<rect key={`${gi}${k}`} x={bx} y={vy} width={bw} height={Math.max(1, zeroY - vy)} rx={1.5} fill={col} />);
      e.push(<text key={`${gi}${k}t`} x={bx + bw / 2} y={vy - 4} textAnchor="middle" fontSize={10} fontWeight={600} fill={MUTED} fontFamily={F}>{fmtNum(v, div)}</text>);
    });
    e.push(<text key={`gl${gi}`} x={gx} y={cBottom + 17} textAnchor="middle" fontSize={12} fill={MUTED} fontFamily={F}>{g.label}</text>);
  });
  const rt = (r) => (r != null && Number.isFinite(r) ? `${r.toFixed(2)}×` : '—');
  e.push(<text key="rt" x={CL} y={16} fontSize={12} fill="var(--color-text)" fontFamily={F}>{i18next.t('phrases:p_30d52cf8d1')} <tspan fontWeight={700}>{rt(c.rend)}</tspan> · 2025 {rt(c.rendN1)}</text>);
  e.push(<rect key="lg1" x={CW - CR - 132} y={8} width={10} height={9} rx={2} fill="var(--color-navy)" />); e.push(<text key="lg1t" x={CW - CR - 118} y={16} fontSize={11.5} fill={MUTED} fontFamily={F}>{i18next.t('phrases:p_8a432ab201')}</text>);
  e.push(<rect key="lg2" x={CW - CR - 66} y={8} width={10} height={9} rx={2} fill="var(--color-accent-soft)" />); e.push(<text key="lg2t" x={CW - CR - 52} y={16} fontSize={11.5} fill={MUTED} fontFamily={F}>2025</text>);
  return <svg viewBox={`0 0 ${CW} ${CH}`} width="100%" style={{ display: 'block', height: 'auto' }}>{e}</svg>;
}

function Runway({ kpi, color, div }) {
    const { t } = useTranslation();
const p = kpi.rw || {}; const treso = (p.treso || []);
  if (treso.filter((v) => Number.isFinite(v)).length < 2) return <Empty />;
  const n = treso.length; const slotR = cPlotW / n;
  const fixe = p.fixe || treso.map(() => 0);
  let ymax = Math.max(...fixe, ...treso.filter(Number.isFinite)); let ymin = Math.min(0, ...treso.filter(Number.isFinite));
  const sp = (ymax - ymin) || 1; ymax += sp * 0.12; ymin -= sp * 0.06;
  const y = (v) => CT + cPlotH - ((v - ymin) / (ymax - ymin)) * cPlotH;
  const zeroY = y(0); const bw = slotR * 0.5; const e = [];
  e.push(<line key="z" x1={CL} x2={CW - CR} y1={zeroY} y2={zeroY} stroke={GRID} strokeWidth={1} />);
  for (let i = 0; i < n; i++) {
    const cx = CL + (i + 0.5) * slotR;
    e.push(<rect key={`fx${i}`} x={cx - bw / 2} y={y(fixe[i])} width={bw} height={Math.max(0.5, zeroY - y(fixe[i]))} fill="var(--color-accent-soft)" stroke="var(--color-surface-card)" strokeWidth={0.5} />);
  }
  const pts = treso.map((v, i) => (Number.isFinite(v) ? [CL + (i + 0.5) * slotR, y(v)] : null)).filter(Boolean);
  e.push(<path key="tl" d={pts.map((q, i) => (i ? 'L' : 'M') + q[0].toFixed(1) + ' ' + q[1].toFixed(1)).join(' ')} fill="none" stroke={color} strokeWidth={2.6} strokeLinejoin="round" strokeLinecap="round" />);
  pts.forEach((q, i) => e.push(<circle key={`tc${i}`} cx={q[0]} cy={q[1]} r={2.2} fill={color} />));
  // Croisement trésorerie = 0 → repère runway.
  let cross = -1;
  for (let i = 0; i < n - 1; i++) { if (Number.isFinite(treso[i]) && Number.isFinite(treso[i + 1]) && treso[i] >= 0 && treso[i + 1] < 0) { cross = i + treso[i] / (treso[i] - treso[i + 1]); break; } }
  if (cross >= 0) {
    const cx = CL + (cross + 0.5) * slotR;
    e.push(<line key="rw" x1={cx} x2={cx} y1={CT} y2={cBottom} stroke={color} strokeWidth={1} strokeDasharray="4 3" opacity={0.7} />);
    e.push(<circle key="rwd" cx={cx} cy={zeroY} r={3} fill={color} />);
  }
  (p.months || []).forEach((lb, i) => e.push(<text key={`ml${i}`} x={CL + (i + 0.5) * slotR} y={cBottom + 17} textAnchor="middle" fontSize={11} fill={MUTED} fontFamily={F}>{lb}</text>));
  e.push(<rect key="l1" x={CL} y={8} width={11} height={9} rx={2} fill="var(--color-accent-soft)" />);
  e.push(<text key="l1t" x={CL + 15} y={16} fontSize={12} fill={MUTED} fontFamily={F}>{i18next.t('phrases:p_8c94fdd695')}</text>);
  e.push(<line key="l3" x1={CL + 128} x2={CL + 150} y1={12.5} y2={12.5} stroke={color} strokeWidth={2.6} strokeLinecap="round" />);
  e.push(<text key="l3t" x={CL + 154} y={16} fontSize={12} fill={MUTED} fontFamily={F}>{t('phrases:p_e0e75da7e7')}</text>);
  return <svg viewBox={`0 0 ${CW} ${CH}`} width="100%" style={{ display: 'block', height: 'auto' }}>{e}</svg>;
}

function Pills({ kpi }) {
  const pills = (kpi.pills || []).filter((p) => Number.isFinite(p.val));
  if (!pills.length) return <Empty />;
  const W = CW; const H = CH; const cPlotWp = W - CL - CR; const cPlotHp = H - CT - CB;
  const nn = pills.length; const cw = cPlotWp / nn; const pw = 42; const ph = cPlotHp - 24; const topY = CT + 22;
  const e = [];
  pills.forEach((p, i) => {
    const cx = CL + (i + 0.5) * cw;
    const scaleY = (v) => topY + ph - Math.max(0, Math.min(1, v / p.max)) * ph;
    e.push(<rect key={`tk${i}`} x={cx - pw / 2} y={topY} width={pw} height={ph} rx={pw / 2} fill="var(--color-border-inner)" />);
    const fy = scaleY(p.val); const fillColor = p.val >= p.ref ? 'var(--color-sage)' : 'var(--color-amber)';
    e.push(<path key={`fl${i}`} d={`M ${cx - pw / 2} ${Math.max(fy, topY + pw / 2)} L ${cx - pw / 2} ${topY + ph - pw / 2} A ${pw / 2} ${pw / 2} 0 0 0 ${cx + pw / 2} ${topY + ph - pw / 2} L ${cx + pw / 2} ${Math.max(fy, topY + pw / 2)} Z`} fill={fillColor} />);
    e.push(<text key={`pv${i}`} x={cx} y={topY - 8} textAnchor="middle" fontSize={18} fontWeight={700} fill="var(--color-text)" fontFamily={F}>{p.val.toFixed(2)}</text>);
    const ry = scaleY(p.ref);
    e.push(<line key={`rf${i}`} x1={cx - pw / 2 - 5} x2={cx + pw / 2 + 5} y1={ry} y2={ry} stroke={INK} strokeWidth={1.6} strokeDasharray="4 3" />);
    e.push(<text key={`rl${i}`} x={cx + pw / 2 + 8} y={ry + 3.5} fontSize={11} fill={MUTED} fontFamily={F}>{i18next.t('phrases:p_a8ee11be07')} {p.ref}</text>);
    e.push(<text key={`ll${i}`} x={cx} y={topY + ph + 16} textAnchor="middle" fontSize={12.5} fontWeight={600} fill="var(--color-text)" fontFamily={F}>{p.label}</text>);
    e.push(<text key={`ld${i}`} x={cx} y={topY + ph + 29} textAnchor="middle" fontSize={11.5} fill={MUTED} fontFamily={F}>{p.deg}</text>);
  });
  return <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block', height: 'auto' }}>{e}</svg>;
}

/* Pas de grille « joli » (1/2/2.5/5 × 10^k). */
function niceStep(raw) {
  if (!Number.isFinite(raw) || raw <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(raw)));
  const f = raw / pow;
  const nice = f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10;
  return nice * pow;
}
